import React, { useEffect, useMemo, useState } from "react";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";
import "./AdminBotInsertsPage.css";

type BotQuestionLog = {
  number?: number;
  question?: string;
  status?: string;
  match_mode?: string | null;
  answer_number?: number;
  answer_text?: string;
  options?: string[];
};

type BotInsertLog = {
  id: string;
  createdAt: string;
  ip?: string;
  userAgent?: string;
  parsedCount?: number;
  foundCount?: number;
  percent?: number;
  mode?: string;
  specialty?: string;
  clipboardText?: string;
  questions?: BotQuestionLog[];
  generatedJs?: string;
  raw?: unknown;
};

const MOSCOW_TIME_ZONE = "Europe/Moscow";

const formatDateTime = (value: string) =>
  value
    ? new Date(value).toLocaleString("ru-RU", {
        timeZone: MOSCOW_TIME_ZONE,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "-";

const shortText = (value = "", limit = 120) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > limit ? `${normalized.slice(0, limit)}...` : normalized || "-";
};

const percentClass = (value = 0) => {
  if (value >= 90) return "admin-percent-good";
  if (value >= 70) return "admin-percent-warning";
  if (value > 0) return "admin-percent-bad";
  return "admin-percent-empty";
};

const AdminBotInsertsPage: React.FC = () => {
  const [logs, setLogs] = useState<BotInsertLog[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const selectedLog = useMemo(
    () => logs.find((log) => log.id === selectedId) || null,
    [logs, selectedId]
  );

  const loadLogs = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/bot-inserts/logs");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось загрузить логи");
      }

      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось загрузить логи");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <AdminNav section="clipboard" />

        <h2>Вставки бота</h2>

        <div className="admin-card admin-toolbar">
          <button
            className="admin-button admin-button-primary"
            onClick={loadLogs}
            disabled={isLoading}
          >
            Обновить
          </button>
          {message && <span className="admin-message">{message}</span>}
        </div>

        <div className="admin-table-wrap bot-inserts-table-wrap">
          <table className="admin-table bot-inserts-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>IP</th>
                <th>Вопросов</th>
                <th>Найдено</th>
                <th>Процент</th>
                <th>Режим</th>
                <th>Специальность</th>
                <th>Фрагмент</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="admin-table-empty">
                    Нет записей
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDateTime(log.createdAt)}</td>
                    <td>{log.ip || "-"}</td>
                    <td>{log.parsedCount ?? 0}</td>
                    <td>{log.foundCount ?? 0}</td>
                    <td>
                      <span className={`admin-percent-badge ${percentClass(log.percent)}`}>
                        {log.percent ?? 0}%
                      </span>
                    </td>
                    <td>{log.mode || "-"}</td>
                    <td>{log.specialty || "-"}</td>
                    <td className="bot-inserts-fragment">{shortText(log.clipboardText)}</td>
                    <td>
                      <button
                        className="admin-button admin-button-small"
                        onClick={() => setSelectedId(log.id)}
                      >
                        Подробнее
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedLog && (
          <div className="admin-modal-backdrop" onClick={() => setSelectedId("")}>
            <div className="admin-modal bot-inserts-modal" onClick={(event) => event.stopPropagation()}>
              <div className="bot-inserts-modal-header">
                <h3>Детали вставки</h3>
                <button className="admin-button admin-button-small" onClick={() => setSelectedId("")}>
                  Закрыть
                </button>
              </div>

              <dl className="admin-details-list">
                <div>
                  <dt>Дата</dt>
                  <dd>{formatDateTime(selectedLog.createdAt)}</dd>
                </div>
                <div>
                  <dt>IP</dt>
                  <dd>{selectedLog.ip || "-"}</dd>
                </div>
                <div>
                  <dt>User-Agent</dt>
                  <dd>{selectedLog.userAgent || "-"}</dd>
                </div>
              </dl>

              <h3>Исходный текст</h3>
              <pre className="bot-inserts-pre">{selectedLog.clipboardText || "-"}</pre>

              <h3>Найденные ответы</h3>
              <div className="bot-inserts-answers">
                {(selectedLog.questions || []).length === 0 ? (
                  <div className="admin-muted">Нет деталей по вопросам</div>
                ) : (
                  (selectedLog.questions || []).map((question, index) => (
                    <div className="bot-inserts-answer" key={`${question.number || index}-${question.question || ""}`}>
                      <strong>
                        {question.number || index + 1}. {question.answer_number ? `${question.answer_number}. ` : ""}
                        {question.answer_text || question.status || "Нет ответа"}
                      </strong>
                      <span>{question.match_mode || question.status || "-"}</span>
                      <p>{question.question || ""}</p>
                    </div>
                  ))
                )}
              </div>

              <h3>Сгенерированный JS</h3>
              <pre className="bot-inserts-pre">{selectedLog.generatedJs || "-"}</pre>

              <h3>Полный JSON</h3>
              <pre className="bot-inserts-pre">
                {JSON.stringify(selectedLog.raw || selectedLog, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBotInsertsPage;
